import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/use-current-user';
import { Header, ContentContainer, Card, CardContent } from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { 
  Users,
  Building2,
  Activity,
  LogIn,
  GraduationCap,
  Trash2,
  Key,
  Wrench,
  Award,
} from '@/lib/icons';

interface ReportCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  allowedLevels?: string[];
}

export function ReportsPage() {
  const navigate = useNavigate();
  const { data: currentAdmin } = useCurrentUser();
  const { setShowPageHeader } = usePageContext();

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  
  const customerReports: ReportCard[] = [
    {
      title: 'User List by Tool',
      description: 'Users with access to specific tools/products',
      icon: <Wrench className="h-6 w-6" />,
      path: '/reports/user-list-by-tool',
    },
    {
      title: 'Portal Activity',
      description: 'Track user activities within the portal',
      icon: <Activity className="h-6 w-6" />,
      path: '/reports/portal-activity',
    },
    {
      title: 'Login Count',
      description: 'User login history and frequency',
      icon: <LogIn className="h-6 w-6" />,
      path: '/reports/login-count',
    },
    {
      title: 'eLearning Tracking',
      description: 'Monitor eLearning course progress and completion',
      icon: <GraduationCap className="h-6 w-6" />,
      path: '/reports/elearning-tracking',
    },
    {
      title: 'User List',
      description: 'Complete list of all portal users',
      icon: <Users className="h-6 w-6" />,
      path: '/reports/user-list',
    },
    {
      title: 'Deleted Users',
      description: 'History of deleted users',
      icon: <Trash2 className="h-6 w-6" />,
      path: '/reports/deleted-users',
      allowedLevels: ['super', 'admin', 'data'],
    },
  ];
  
  const evaluationReports: ReportCard[] = [
    {
      title: 'PCACP Exam Results',
      description: 'View PCACP exam attempt history, scores, and pass/fail outcomes by user.',
      icon: <Award className="h-6 w-6" />,
      path: '/reports/pcacp-exam-results',
      allowedLevels: ['super', 'data'],
    },
  ];

  const organizationReports: ReportCard[] = [
    {
      title: 'Organization List',
      description: 'List of all organizations with license details',
      icon: <Building2 className="h-6 w-6" />,
      path: '/reports/organization-list',
      allowedLevels: ['super', 'admin', 'data'],
    },
    {
      title: 'Workspace Entitlements',
      description: 'Track workspace access changes',
      icon: <Key className="h-6 w-6" />,
      path: '/reports/workspace-entitlements',
    },
  ]
  
  const isAllowed = (report: ReportCard) => {
    if (!report.allowedLevels) return true;
    return currentAdmin?.level && report.allowedLevels.includes(currentAdmin.level);
  };
  
  const visibleCustomerReports = customerReports.filter(isAllowed);
  const visibleEvaluationReports = evaluationReports.filter(isAllowed);
  const visibleOrgReports = organizationReports.filter(isAllowed);
  
  return (
    <div>
      <Header 
        title="Reports" 
        size="small"
      />
      <ContentContainer>
        {/* Customer Reports */}
        <div>
          <h2 className="text-3xl font-normal font-serif mb-4">Customer Reports</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleCustomerReports.map((report) => (
              <Card
                key={report.path}
                className="flex justify-center"
                onClick={() => navigate(report.path)}
                size="medium"
                isClickable
              >
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-none text-primary bg-accent-lilac/50">
                      {report.icon}
                    </div>
                    <div>
                      <h3 className="font-serif text-xl">
                        {report.title}
                      </h3>
                      <p className="text-sm">{report.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Evaluations Reports */}
        {visibleEvaluationReports.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Evaluations Reports</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleEvaluationReports.map((report) => (
                <Card
                  key={report.path}
                  className="flex justify-center"
                  onClick={() => navigate(report.path)}
                  size="medium"
                  isClickable
                >
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center flex-none text-primary">
                        {report.icon}
                      </div>
                      <div>
                        <h3>
                          {report.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">{report.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Organization Reports */}
        <div>
          <h2 className="text-3xl font-normal font-serif mb-4">Organization Reports</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleOrgReports.map((report) => (
              <Card
                key={report.path}
                className="flex justify-center"
                onClick={() => navigate(report.path)}
                size="medium"
                isClickable
              >
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-none text-primary bg-accent-lilac/50">
                      {report.icon}
                    </div>
                    <div>
                      <h3 className="font-serif text-xl">
                        {report.title}
                      </h3>
                      <p className="text-sm">{report.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </ContentContainer>
    </div>
  );
}